import { Component } from 'react';
import Scrollbars from 'react-custom-scrollbars-2';
import { connect } from 'react-redux';

import Button from 'components/atoms/Button';
import CheckBox from 'components/atoms/CheckBox';
import DropList from 'components/atoms/DropList';
import Form from 'components/atoms/Form';
import FormGroup from 'components/atoms/FormGroup';
import Icon from 'components/atoms/Icon';
import Input from 'components/atoms/Input';
import Label from 'components/atoms/Label';
import Message from 'components/atoms/Message';
import TypeTag from 'components/atoms/TypeTag';
import { bindActionCreators, Dispatch } from 'redux';
import { chainageData } from 'services/profile';
import {
    SetErrorHandlerAction,
    setErrorHandlerAction,
    ShowMessageAction,
    showMessageAction,
} from 'store/actions/errorsActions';
import { SetLayoutAction, setLayoutAction } from 'store/actions/layoutActions';
import {
    ChainageType,
    InterfaceVariablesType,
    MessageType,
    PlanType,
    PortType,
    ProfileDataType,
    RootState,
    WayPointType,
} from 'types';
import { LayerControlInterface, ProfileControlsInterface, SearchControlsInterface } from 'types/functions';
import { FilterObjectType } from 'types/search';
import { ActionsType } from 'types/system';
import { checkDifferent, clone, getId, renderError, setKey } from 'utils/formHandlers';
import { translate } from 'utils/translate';
import { metersToNauticalMiles, nauticalMilesToMeters } from 'utils/unit';

interface ProfileOptionsModelProps {
    children?: string | JSX.Element | JSX.Element[];
    language: string;
    id?: string;
    data: any;
    pilotagePlans: PlanType[];
    port: PortType;
    actions: ActionsType | undefined;
    searchControls: SearchControlsInterface;
    layerControls: LayerControlInterface;
    searchObject: FilterObjectType;
    setErrorHandlerAction: SetErrorHandlerAction;
    profileControls: ProfileControlsInterface;
    setLayoutAction: SetLayoutAction;
    showMessageAction: ShowMessageAction;
}

interface ProfileOptionsModelState {
    errors: object;
    data: any | null;
    interfaceVariables: InterfaceVariablesType;
    planChnaged: boolean;
    markerPoints: {
        chainage: number;
        display: boolean;
        id: number;
        insidePortArea: boolean;
        name: string;
        parent_id: number;
    }[];
    pilotagePlans: PlanType[];
    waypoints: WayPointType[];
    startPoints: { name: string; value: string }[];
    endPoints: { name: string; value: string }[];
    loading: boolean;
    unit: string;
    chainage: ChainageType[];
    maxChainage: number;
    message: MessageType | null;
    analysisData: ProfileDataType;
}

class ProfileOptionsModel extends Component<ProfileOptionsModelProps, ProfileOptionsModelState> {
    constructor(props: ProfileOptionsModelProps) {
        super(props);
        this.state = {
            interfaceVariables: props.port.meta_data.interfaceVariables,
            planChnaged: false,
            unit: this.props.data.unit || 'point',
            message: null,
            loading: false,
            markerPoints: [],
            chainage: [],
            maxChainage: 0,
            analysisData: {
                pilotagePlanAnalysis: clone(this.props.data.pilotagePlanAnalysis) || {
                    referencePoints: [],
                    pilotagePlan:
                        this.props.pilotagePlans.find((x: PlanType) => {
                            return x.value;
                        }) || null,
                    startPoint: 0,
                    endPoint: 0,
                    startChainage: 0,
                    endChainage: 0,
                    items: {
                        xtd: true,
                        sog: true,
                        swpxtd: false,
                        heading: false,
                        cog: false,
                        drift: false,
                        rot: false,
                        crot: false,
                    },
                },
            },

            data: props.data,
            errors: {},
            waypoints: [],
            pilotagePlans: props.pilotagePlans,
            startPoints: [],
            endPoints: [],
        };
    }

    static getDerivedStateFromProps(props: ProfileOptionsModelProps, state: ProfileOptionsModelState) {
        if (checkDifferent(props.pilotagePlans, state.pilotagePlans)) {
            return {
                pilotagePlans: props.pilotagePlans,
            };
        }

        return null;
    }

    componentDidMount(): void {
        this.prepareData(false);
    }

    handleChange = (name: string, value: unknown) => {
        const { data, errors } = this.state;
        setKey(data, name, value);
        setKey(errors, name, null);
        this.setState({ data, errors });
    };

    handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e?.preventDefault();
        this.props.profileControls.setProfileData(
            this.prepareSend(this.state.analysisData),
            this.state.chainage,
            this.state.unit
        );
        this.props.setLayoutAction([
            { action: 'UPDATE_SECTION', data: { name: 'profile', state: true, display: true, disable: false } },
        ]);
        this.props.actions?.onCancel();
    };

    handleFormCancel = () => {
        this.props.actions?.onCancel();
    };

    updateItem = (group: string, key: string) => {
        const { analysisData } = this.state;

        analysisData[group].items[key] = !analysisData[group].items[key];

        this.setState({ analysisData }, () => {
            this.validate();
        });
    };

    validate = () => {
        const { analysisData, markerPoints, pilotagePlans } = this.state;
        const foundNumbers = this.props.searchControls.getFoundObjectNumbers();

        let message: MessageType | null = null;

        const startPoint = markerPoints.find((x) => {
            return x.id === analysisData.pilotagePlanAnalysis.startPoint;
        });
        const endPoint = markerPoints.find((x) => {
            return x.id === analysisData.pilotagePlanAnalysis.endPoint;
        });
        if (!foundNumbers && this.props.searchObject.limit === 0) {
            message = {
                type: 'error',
                text: translate('No search defined. Please define a search before attempting profile'),
            };
        } else if (pilotagePlans.length === 0) {
            message = {
                type: 'error',
                text: translate('This system has no pilotage plans'),
            };
        } else if (foundNumbers?.total == 0) {
            message = {
                type: 'warning',
                text: translate('Search contains zero transits, no data to analyse. Please update your search.'),
            };
        } else if (
            this.props.searchObject.limit === 0 &&
            //@ts-ignore
            foundNumbers?.total - foundNumbers.forReview > this.state.interfaceVariables.maxSearchLimit_transits * 10
        ) {
            message = {
                type: 'warning',
                text: translate('large analysis request. please reduce the search to less than transits.', {
                    maxSearchLimit_transits: this.state.interfaceVariables.maxSearchLimit_transits * 10,
                }),
            };
        } else if (
            this.props.searchObject.limit === 0 &&
            //@ts-ignore
            foundNumbers?.total - foundNumbers.forReview > this.state.interfaceVariables.maxSearchLimit_transits * 10
        ) {
            message = {
                type: 'warning',
                text: translate('large analysis request. please reduce the search to less than transits.', {
                    maxSearchLimit_transits: this.state.interfaceVariables.maxSearchLimit_transits * 10,
                }),
            };
        } else if (!analysisData.pilotagePlanAnalysis.pilotagePlan?.name) {
            message = { type: 'warning', text: translate('Please select a pilotage plan for this profile.') };
        } else if (!analysisData.pilotagePlanAnalysis.pilotagePlan.route) {
            message = { type: 'warning', text: translate('This plan has no route defined.') };
        } else if (
            Object.keys(analysisData.pilotagePlanAnalysis.items).filter((x) => {
                return analysisData.pilotagePlanAnalysis.items[x];
            }).length === 0
        ) {
            message = { type: 'warning', text: translate('You must select at least one option to run the profile.') };
        } else if (
            this.state.unit === 'point' &&
            analysisData.pilotagePlanAnalysis.startPoint != null &&
            analysisData.pilotagePlanAnalysis.endPoint != null &&
            this.state.markerPoints.length >= 1 &&
            endPoint &&
            startPoint &&
            endPoint.chainage < startPoint.chainage
        ) {
            message = { type: 'warning', text: translate('End point must be after start point.') };
            // } else if (
            //     this.state.unit === 'point' &&
            //     analysisData.pilotagePlanAnalysis.startPoint != null &&
            //     analysisData.pilotagePlanAnalysis.endPoint != null &&
            //     analysisData.pilotagePlanAnalysis.endPoint == analysisData.pilotagePlanAnalysis.startPoint
            // ) {
            //     message = {
            //         type: 'warning',
            //         text: translate(
            //             'The starting point and ending point cannot be the same. Please choose different points.'
            //         ),
            //     };
        } else if (
            this.state.unit === 'chainage' &&
            (analysisData.pilotagePlanAnalysis.endChainage === null ||
                isNaN(analysisData.pilotagePlanAnalysis.endChainage) ||
                analysisData.pilotagePlanAnalysis.startChainage === null ||
                isNaN(analysisData.pilotagePlanAnalysis.startChainage))
        ) {
            message = { type: 'warning', text: 'Please select chainage values.' };
        } else if (
            this.state.unit === 'chainage' &&
            analysisData.pilotagePlanAnalysis.endChainage != null &&
            analysisData.pilotagePlanAnalysis.startChainage != null &&
            analysisData.pilotagePlanAnalysis.endChainage < analysisData.pilotagePlanAnalysis.startChainage
        ) {
            message = { type: 'warning', text: translate('End chainage must be less than start chainage.') };
            // } else if (
            //     this.state.unit === 'chainage' &&
            //     analysisData.pilotagePlanAnalysis.endChainage != null &&
            //     analysisData.pilotagePlanAnalysis.startChainage != null &&
            //     analysisData.pilotagePlanAnalysis.endChainage == analysisData.pilotagePlanAnalysis.startChainage
            // ) {
            //     message = {
            //         type: 'warning',
            //         text: translate(
            //             'The start chainage and end chainage cannot be the same. Please choose different values.'
            //         ),
            //     };
        } else if (
            this.state.unit === 'chainage' &&
            analysisData.pilotagePlanAnalysis.endChainage != null &&
            analysisData.pilotagePlanAnalysis.startChainage != null &&
            this.state.maxChainage != null &&
            analysisData.pilotagePlanAnalysis.endChainage > this.state.maxChainage
        ) {
            message = {
                type: 'warning',
                text: translate('The end chainage must be less than route length.', {
                    maxChainage: metersToNauticalMiles(this.state.maxChainage),
                }),
            };
        } else if (
            this.props.searchObject.limit === 0 &&
            //@ts-ignore
            foundNumbers?.total - foundNumbers.forReview > this.state.interfaceVariables.maxSearchLimit_transits
        ) {
            this.props.showMessageAction({
                messageType: 'WARN',
                message: translate(
                    'this search exceeds the recommended limit of transits. The operation may take longer to complete.',
                    {
                        maxSearchLimit: this.state.interfaceVariables.maxSearchLimit_transits,
                    }
                ),
                options: {
                    autoClose: true,
                    hideProgressBar: true,
                },
            });
        }

        this.setState({ message });

        return message === null;
    };

    prepareSend = (data: any) => {
        data.pilotagePlanAnalysis = {
            ...data.pilotagePlanAnalysis,
            endChainage: nauticalMilesToMeters(data.pilotagePlanAnalysis.endChainage),
            startChainage: nauticalMilesToMeters(data.pilotagePlanAnalysis.startChainage),
        };
        return data;
    };

    prepareData = (update: boolean) => {
        console.log('prepareData', update);
        const { analysisData } = this.state;

        if (!analysisData.pilotagePlanAnalysis.pilotagePlan) {
            this.validate();
            return false;
        }

        this.setState({ loading: true }, () => {
            chainageData(this.props.port.id, {
                analysisData: analysisData,
                id: getId(),
            })
                .then((res) => {
                    const markerPoints = res.items
                        .map((point: any) => {
                            return { ...point, display: true };
                        })
                        .filter((point: any) => {
                            return point.insidePortArea;
                        })
                        .sort((a: any, b: any) => (a.chainage > b.chainage ? 1 : -1));

                    const { startPoint, endPoint } = markerPoints.reduce((acc: any, curr: any) => {
                        if (!acc.startPoint || curr.chainage <= acc.startPoint.chainage) {
                            acc.startPoint = curr;
                        }
                        if (!acc.endPoint || curr.chainage >= acc.endPoint.chainage) {
                            acc.endPoint = curr;
                        }
                        return acc;
                    }, {});

                    if (update || !this.props.data.pilotagePlanAnalysis) {
                        analysisData.pilotagePlanAnalysis = {
                            ...analysisData.pilotagePlanAnalysis,
                            referencePoints: markerPoints.map((x) => {
                                return x.id;
                            }),
                            startPoint: startPoint?.id || null,
                            endPoint: endPoint?.id || null,
                            startChainage: startPoint ? metersToNauticalMiles(startPoint.chainage) : 0,
                            endChainage: endPoint
                                ? metersToNauticalMiles(endPoint.chainage)
                                : metersToNauticalMiles(res.length),
                        };
                    } else {
                        analysisData.pilotagePlanAnalysis = {
                            ...analysisData.pilotagePlanAnalysis,
                            referencePoints: markerPoints.map((x: any) => {
                                return x.id;
                            }),
                            startPoint: startPoint?.id || null,
                            endPoint: endPoint?.id || null,
                            startChainage: metersToNauticalMiles(analysisData.pilotagePlanAnalysis.startChainage),
                            endChainage: metersToNauticalMiles(analysisData.pilotagePlanAnalysis.endChainage),
                        };
                    }

                    this.setState(
                        {
                            chainage: res.items.sort((a, b) => (a.chainage > b.chainage ? 1 : -1)),
                            markerPoints,
                            analysisData,
                            maxChainage: metersToNauticalMiles(res.length),
                            unit: update ? (markerPoints.length > 0 ? 'point' : 'chainage') : this.state.unit,
                        },
                        this.validate
                    );
                })
                .catch((error) => {
                    this.props.setErrorHandlerAction(error);
                });
        });
    };

    selectReferencePoints = (selectReferencePoint: any) => {
        const { analysisData } = this.state;
        if (analysisData.pilotagePlanAnalysis.referencePoints.includes(selectReferencePoint)) {
            analysisData.pilotagePlanAnalysis.referencePoints.splice(
                analysisData.pilotagePlanAnalysis.referencePoints.indexOf(selectReferencePoint),
                1
            );
        } else {
            analysisData.pilotagePlanAnalysis.referencePoints.push(selectReferencePoint);
        }

        this.setState({ analysisData });
    };

    handelChangePlan = (name: any, value: any) => {
        const selected = this.state.pilotagePlans.find((x) => {
            return x.key == value;
        });

        this.setState(
            {
                planChnaged: true,
                analysisData: {
                    pilotagePlanAnalysis: {
                        pilotagePlan: selected,
                        referencePoints: [],
                        startPoint: 0,
                        endPoint: 0,
                        items: this.state.analysisData.pilotagePlanAnalysis.items,
                        startChainage: 0,
                        endChainage: 0,
                    },
                },
            },
            () => {
                this.prepareData(true);
                this.props.layerControls.togglePlan(selected?.key);
            }
        );
    };

    handelChangeLine = (name: string | number, value: string) => {
        const { analysisData } = this.state;

        // Validate that the input is a number and has a maximum of 2 decimal places
        if (/^\d*\.?\d{0,2}$/.test(value)) {
            analysisData.pilotagePlanAnalysis[name] = parseFloat(value);
            this.setState({ analysisData }, () => {
                this.validate();
                this.updateReferencePoints();
            });
        }
    };

    updateReferencePoints = () => {
        const { markerPoints, analysisData } = this.state;

        const startPoint = markerPoints.find((x) => {
            return x.id === analysisData.pilotagePlanAnalysis.startPoint;
        });
        const endPoint = markerPoints.find((x) => {
            return x.id === analysisData.pilotagePlanAnalysis.endPoint;
        });

        if (!startPoint || !endPoint) {
            return;
        }

        analysisData.pilotagePlanAnalysis.referencePoints = markerPoints
            .filter((x) => {
                if (this.state.unit === 'point') {
                    return x.chainage >= startPoint.chainage && x.chainage <= endPoint.chainage;
                } else {
                    return (
                        x.chainage >= nauticalMilesToMeters(analysisData.pilotagePlanAnalysis.startChainage, 10) - 10 &&
                        x.chainage <= nauticalMilesToMeters(analysisData.pilotagePlanAnalysis.endChainage, 10) + 10
                    );
                }
            })
            .map((x) => {
                return x.id;
            });

        if (this.state.unit === 'point') {
            analysisData.pilotagePlanAnalysis.startChainage = metersToNauticalMiles(startPoint.chainage);
            analysisData.pilotagePlanAnalysis.endChainage = metersToNauticalMiles(endPoint.chainage);
        }

        this.setState({
            analysisData,
            markerPoints: markerPoints.map((x) => {
                return { ...x, display: x.chainage >= startPoint.chainage && x.chainage <= endPoint.chainage };
            }),
        });
    };

    switchUnit = () => {
        if (this.state.markerPoints.length === 0) {
            // this.context.showCodeMessage('Warning', `No waypoints to used.`, 'warning')
            return;
        }
        this.setState({ unit: this.state.unit === 'point' ? 'chainage' : 'point' });
    };

    render() {
        return (
            <>
                <Form onSubmit={this.handleFormSubmit} className="bg-white shadow p-4 w-full rounded">
                    <div id={`Drag-${this.props.id}`} className="flex justify-between mb-2 no-select ">
                        <div className="text-lg leading-6 font-bold">{translate('update analysis')} </div>
                        <div
                            onTouchEnd={this.handleFormCancel}
                            onClick={this.handleFormCancel}
                            className="cursor-pointer"
                        >
                            <Icon>
                                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                <path d="M18 6l-12 12" />
                                <path d="M6 6l12 12" />
                            </Icon>
                        </div>
                    </div>
                    {this.state.message && (
                        <div>
                            <Message type={this.state.message.type}>{this.state.message.text}</Message>
                        </div>
                    )}

                    <FormGroup comments={renderError(this.state.errors?.['plan'])} required={false}>
                        <Label>{translate('Plan')}</Label>
                        <DropList
                            capitalize
                            id="plan"
                            name="plan"
                            options={this.state.pilotagePlans.map((x) => {
                                return { value: x.key, label: x.name };
                            })}
                            value={this.state.analysisData.pilotagePlanAnalysis.pilotagePlan?.key || ''}
                            onChange={this.handelChangePlan}
                        />
                    </FormGroup>

                    <FormGroup>
                        <div className=" grid grid-cols-2 gap-2 justify-between items-center ">
                            <Label>
                                Start {this.state.unit} {this.state.unit === 'chainage' && '(NM)'}
                            </Label>
                            <Label>
                                End {this.state.unit} {this.state.unit === 'chainage' && '(NM)'}
                            </Label>
                        </div>

                        <div className=" grid grid-cols-2 gap-5 justify-between items-center ">
                            <div className="flex">
                                <div className="relative">
                                    <div onClick={this.switchUnit}>
                                        <Icon className=" cursor-pointer mr-1  h-6 w-6 mt-1 ">
                                            {this.state.unit === 'point' ? (
                                                <>
                                                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                    <path d="M17 3l4 4l-14 14l-4 -4z" />
                                                    <path d="M16 7l-1.5 -1.5" />
                                                    <path d="M13 10l-1.5 -1.5" />
                                                    <path d="M10 13l-1.5 -1.5" />
                                                    <path d="M7 16l-1.5 -1.5" />
                                                </>
                                            ) : (
                                                <>
                                                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                    <path d="M9 11a3 3 0 1 0 6 0a3 3 0 0 0 -6 0" />
                                                    <path d="M17.657 16.657l-4.243 4.243a2 2 0 0 1 -2.827 0l-4.244 -4.243a8 8 0 1 1 11.314 0z" />
                                                </>
                                            )}
                                        </Icon>
                                    </div>
                                </div>
                                {this.state.unit === 'chainage' ? (
                                    <Input
                                        value={this.state.analysisData.pilotagePlanAnalysis.startChainage}
                                        name="startChainage"
                                        type="number"
                                        onChange={this.handelChangeLine}
                                        className=" w-full px-4 py-2 border border-gray-400 leading-5 font-medium rounded-md text-gray-700 bg-white hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:text-gray-800 active:bg-gray-50 transition ease-in-out duration-150"
                                    ></Input>
                                ) : (
                                    <DropList
                                        capitalize
                                        options={this.state.markerPoints.map((item, index) => {
                                            return { value: item.id, label: item.name };
                                        })}
                                        name="startPoint"
                                        id="startPoint"
                                        value={this.state.analysisData.pilotagePlanAnalysis.startPoint}
                                        onChange={this.handelChangeLine}
                                    />
                                )}
                            </div>
                            {this.state.unit === 'chainage' ? (
                                <Input
                                    value={this.state.analysisData.pilotagePlanAnalysis.endChainage}
                                    name="endChainage"
                                    type="number"
                                    onChange={this.handelChangeLine}
                                    className=" w-full px-4 py-2 border border-gray-400 leading-5 font-medium rounded-md text-gray-700 bg-white hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:text-gray-800 active:bg-gray-50 transition ease-in-out duration-150"
                                ></Input>
                            ) : (
                                <DropList
                                    capitalize
                                    options={this.state.markerPoints.map((item, index) => {
                                        return { value: item.id, label: item.name };
                                    })}
                                    name="endPoint"
                                    id="endPoint"
                                    value={this.state.analysisData.pilotagePlanAnalysis.endPoint}
                                    onChange={this.handelChangeLine}
                                />
                            )}
                        </div>
                    </FormGroup>

                    <FormGroup>
                        <Label>{translate('Parameters to analyse')}</Label>
                        <div className=" grid grid-cols-4 justify-between items-center ">
                            {Object.keys(this.state.analysisData.pilotagePlanAnalysis.items).map((key, index) => {
                                return (
                                    <div
                                        key={key}
                                        className={`flex  ${
                                            index === 3 || index === 5 ? 'capitalize' : 'uppercase'
                                        } justify-start items-center gap-2 mr-2 `}
                                    >
                                        {key} :
                                        <CheckBox
                                            value={
                                                this.state.analysisData.pilotagePlanAnalysis.items[key] ? true : false
                                            }
                                            onChange={() => {
                                                this.updateItem('pilotagePlanAnalysis', key);
                                            }}
                                            name={key}
                                        ></CheckBox>
                                    </div>
                                );
                            })}
                        </div>
                    </FormGroup>

                    <FormGroup>
                        <Label>{translate('Reference points to display')}</Label>
                        <Scrollbars
                            autoHide
                            renderThumbHorizontal={(props) => <div {...props} style={{ display: 'none' }} />}
                            style={{ maxHeight: '135px', minHeight: '70px' }}
                            className=" pr-2 overflow-hidden"
                        >
                            {this.state.markerPoints.length === 0 ? (
                                <div>No reference points</div>
                            ) : (
                                <div className="mt-1 ">
                                    {this.state.markerPoints
                                        .filter((x) => {
                                            return x.display;
                                        })
                                        .map((x, i) => {
                                            return (
                                                <span
                                                    key={i}
                                                    onClick={() => {
                                                        this.selectReferencePoints(x.id);
                                                    }}
                                                >
                                                    <TypeTag
                                                        active={this.state.analysisData.pilotagePlanAnalysis.referencePoints?.includes(
                                                            x.id
                                                        )}
                                                        type="user"
                                                        className="float-left mr-1 mb-1 "
                                                    >
                                                        {x.name}
                                                    </TypeTag>
                                                </span>
                                            );
                                        })}
                                </div>
                            )}
                        </Scrollbars>
                    </FormGroup>

                    <div className="flex justify-end">
                        <div className="mt-5 flex gap-3">
                            <Button
                                className="px-4 py-2 font-bold border border-gray-400"
                                onClick={this.handleFormCancel}
                            >
                                {translate('Cancel')}
                            </Button>
                            <Button
                                disabled={this.state.message !== null}
                                type="submit"
                                className="px-4 py-2 bg-primary-submit rounded-md text-white"
                            >
                                {translate('Profile')}
                            </Button>
                        </div>
                    </div>
                </Form>
            </>
        );
    }
}

const profileOptionsModelStateToProps = (state: RootState) => ({
    language: state.user.user.language,
    port: state.port.port,
    pilotagePlans: state.port.pilotagePlans,
    searchControls: state.layout.searchControls,
    layerControls: state.layout.layerControls,
    profileControls: state.layout.profileControls,
    searchObject: state.layout.searchObject,
});

const profileOptionsModelDispatchToProps = (dispatch: Dispatch) => ({
    setErrorHandlerAction: bindActionCreators(setErrorHandlerAction, dispatch),
    setLayoutAction: bindActionCreators(setLayoutAction, dispatch),
    showMessageAction: bindActionCreators(showMessageAction, dispatch),
});

export default connect(profileOptionsModelStateToProps, profileOptionsModelDispatchToProps)(ProfileOptionsModel);
